import Handsontable from 'handsontable';
import { CellData, TableContentData } from '../../../../../common/table/TableTypes';
import { cloneDeep } from 'lodash';
import { buildHotTableContentData } from './hotTableDataUtils';
import { getFirstSelection, getStartCol, getStartRow } from '../selectionUtils/tableSelectionAccessors';
import { excludeRefHeadersFromSelection, getAllCellsBetween } from '../selectionUtils/tableSelectionGeometryUtils';
import { isBackgroundStyle, isColorStyle } from '../../../../components/editor/editorUtils';
import { convertFromRaw, convertToRaw, EditorState, Modifier } from 'draft-js';
import getSelectionStateForContent from '../../../../components/editor/customRichUtils/selection/getSelectionStateForContent';
import {
    TEXT_BACKGROUND_PRESETS,
    TEXT_COLOR_PRESETS,
} from '../../../../components/editor/plugins/textColor/textColorConstants';
import { doesTableCellDataHaveInlineStyles } from '../dataUtils/cell/tableCellDraftContentUtils';
import rawGetText from '../../../../../common/utils/editor/rawUtils/rawGetText';
import { setHotTableCellMetaObject } from './meta/hotTableCellMetaUtils';

export type CellDataUpdateFunction = (prevCellData: CellData, row?: number, col?: number) => CellData;
export type CellDataChangeEntry = [row: number, col: number, CellDataUpdateFunction];

/**
 * TODO - Explain the purpose of this function
 * TODO - Remove export once useTableOperations is removed
 */
export const renderAndResizeHotTable = (hotInstance: Handsontable): void => {
    // `requestAnimationFrame` is used for calling render here as it improves performance
    requestAnimationFrame(() => {
        if (hotInstance.isDestroyed) return;

        // Render recent changes
        hotInstance.render();

        // Ensure row heights are correct
        hotInstance.refreshDimensions();
    });
};

/**
 * Sets the cell data in the Handsontable instance based on the provided cell data changes.
 *
 * NOTE: This will *not* automatically trigger a redux sync. You'll need to manually use
 *   hotInstance.runHooks('onMilanoteMarkDirty', source) after this function to trigger the sync.
 *
 * TODO - Review this function to see if it's ideal.
 *   Might be better to separate the data changes vs the saving.
 *   I.e. Have one function that determines the changes to the data
 *    and this function performing the actual updating of the data.
 */
export const setHotTableCellData = (
    hotInstance: Handsontable,
    cellDataChanges: CellDataChangeEntry[],
    source: string,
    // FIXME - Ideally we'd remove the need for this / double check the ordering
    afterRenderAndResizeCallback?: (newData: TableContentData) => void,
): void => {
    if (!cellDataChanges?.length) return;

    const hotTableData = buildHotTableContentData(hotInstance);
    const newData = cloneDeep(hotTableData);

    hotInstance.suspendRender();

    const referencedCellUpdates: Array<[number, number, Handsontable.CellValue]> = [];

    const formulasPlugin = hotInstance.getPlugin('formulas');
    const hyperFormulaEngine = formulasPlugin.engine;
    const sheetId = formulasPlugin.sheetId;

    cellDataChanges.forEach((change) => {
        const [row, col, getCellDataUpdatesFn] = change;

        const cellDataUpdates = getCellDataUpdatesFn(newData[row][col], row, col);

        newData[row][col] = {
            ...newData[row][col],
            ...cellDataUpdates,
        };

        setHotTableCellMetaObject(hotInstance, row, col, { cellData: newData[row][col] });

        // If the current cell has dependants, we need to update the values so that afterChange can run
        if (!!hyperFormulaEngine && !!sheetId) {
            const cellDependents = hyperFormulaEngine.getCellDependents({ sheet: sheetId, row, col });
            if (cellDependents.length) referencedCellUpdates.push([row, col, newData[row][col].value]);
        }
    });

    hotInstance.resumeRender();
    renderAndResizeHotTable(hotInstance);

    // TODO-MOBILE-TABLES - Determine if this ordering is required, or if there's another way that
    //  we can perform updates to avoid the rendering dependencies
    // CALLBACK
    afterRenderAndResizeCallback?.(newData);

    if (referencedCellUpdates.length === 0) return;

    // Update cells that are referenced so that cell type changes can be applied to the dependents
    // This must run after the table element has been updated, because any dependent cell type
    // changes through setDataAtCell > afterChange will also be updated through this function recursively,
    // and we don't want the inner table data updates to happen before the original one
    //
    // There is a Handsontable bug where setDataAtCell only resolves source properly if passed as 4th param,
    // despite the type overloads saying otherwise
    // TODO-HANDSONTABLE-UPGRADE: Check whether this is still a bug after upgrading Handsontable
    // @ts-ignore - This addition was pulled from a bug that Marisa fixed
    hotInstance.setDataAtCell(referencedCellUpdates, undefined, undefined, source);
};

/**
 * Applies or removes the specified text style to all selected cells in the Handsontable instance.
 */
export const applyHotTableTextStyle = (
    hotInstance: Handsontable,
    textStyle: string | null,
    cellSelections = hotInstance.getSelected(),
): TableContentData | undefined => {
    if (!cellSelections) return;

    const hotTableData = buildHotTableContentData(hotInstance);

    // ****************************************************
    // Determine whether to add/remove inline style based on the first cell selection

    const firstCellSelection = getFirstSelection(cellSelections);
    if (!firstCellSelection) return;

    // Remove any header cells from the selection
    const validCellSelection = excludeRefHeadersFromSelection(firstCellSelection);

    const cellData = hotTableData[getStartRow(validCellSelection)][getStartCol(validCellSelection)];

    const shouldAddInlineStyle = textStyle ? !doesTableCellDataHaveInlineStyles(cellData, textStyle) : true;
    const isColorOrBackgroundTextStyleChange = !textStyle || isColorStyle(textStyle) || isBackgroundStyle(textStyle);

    // ****************************************************
    // Apply/remove inline style to all selected cells

    const newData = cloneDeep(hotTableData);

    getAllCellsBetween(cellSelections).forEach((cell) => {
        const { row, col } = cell;
        const cellData = hotTableData[row][col];

        // Add/remove text style from cellData.textStyle

        let newTextStyle = newData[row][col].textStyle || [];
        if (shouldAddInlineStyle) {
            newTextStyle = newTextStyle.filter((currentTextStyle) => {
                if (isColorOrBackgroundTextStyleChange) {
                    return !(isColorStyle(currentTextStyle) || isBackgroundStyle(currentTextStyle));
                }
                return currentTextStyle !== textStyle;
            });

            textStyle && newTextStyle.push(textStyle);
        } else {
            newTextStyle = newTextStyle.filter((style) => style !== textStyle);
        }

        newData[row][col].textStyle = newTextStyle;

        // Add/remove text style from inline style of cellData.draftContent
        if (cellData.draftContent) {
            // If a color/background text style is added, remove all other existing color/background inline text styles
            if (shouldAddInlineStyle && isColorOrBackgroundTextStyleChange) {
                let contentState = convertFromRaw(cellData.draftContent);
                let newEditorState = EditorState.createWithContent(contentState);

                const selectionState = getSelectionStateForContent(contentState);

                [...Object.values(TEXT_COLOR_PRESETS), ...Object.values(TEXT_BACKGROUND_PRESETS)].forEach((style) => {
                    contentState = Modifier.removeInlineStyle(contentState, selectionState, style);
                });

                newEditorState = EditorState.push(newEditorState, contentState, 'change-inline-style');

                newData[row][col].draftContent = convertToRaw(newEditorState.getCurrentContent());
                newData[row][col].value = rawGetText(newData[row][col].draftContent);
            }

            // If the style needs to be removed, ensure that the styling is removed from Draft format as well
            if (!shouldAddInlineStyle && textStyle) {
                const prevContentState = convertFromRaw(cellData.draftContent);

                let newEditorState = EditorState.createWithContent(prevContentState);

                const selectionState = getSelectionStateForContent(prevContentState);

                const newContentState = Modifier.removeInlineStyle(prevContentState, selectionState, textStyle);
                newEditorState = EditorState.push(newEditorState, newContentState, 'change-inline-style');

                newData[row][col].draftContent = convertToRaw(newEditorState.getCurrentContent());
                newData[row][col].value = rawGetText(newData[row][col].draftContent);
            }
        }

        setHotTableCellMetaObject(hotInstance, row, col, { cellData: newData[row][col] });
    });

    renderAndResizeHotTable(hotInstance);

    return newData;
};
